import { useState, useEffect, useRef } from 'react';
import { useStoreSettings, COLOR_PRESETS, applyColorPreset } from '@/hooks/useStoreSettings';
import { notifyDesignChanged } from '@/hooks/useDesignSystem';
import IdentitySection from './design/IdentitySection';
import BrandSection from './design/BrandSection';
import ColorsSection from './design/ColorsSection';
import TypographySection from './design/TypographySection';
import LayoutSection from './design/LayoutSection';
import ComponentsSection from './design/ComponentsSection';
import SectionsSection from './design/SectionsSection';
import {
  loadDesignSettings, loadBrandSettings, loadIdentitySettings,
  saveDesignSettings, saveBrandSettings, saveIdentitySettings,
  DEFAULT_DESIGN, DEFAULT_BRAND, DEFAULT_IDENTITY,
  DesignSettings, BrandSettings, IdentitySettings,
} from './design/designTypes';
import { FONT_OPTIONS, RADIUS_OPTIONS, FOOTER_STYLES, BUTTON_STYLES, NAVBAR_STYLES, LAYOUT_OPTIONS, HERO_IMAGE_PRESETS } from './design/designConstants';
import { useSectionSave, LogoPreview } from './design/DesignShared';

interface DesignCustomizerProps {
  onToast: (msg: string) => void;
}

export default function DesignCustomizer({ onToast }: DesignCustomizerProps) {
  const { settings, updateSettings } = useStoreSettings();
  const [design, setDesign]     = useState<DesignSettings>(loadDesignSettings);
  const [brand, setBrand]       = useState<BrandSettings>(loadBrandSettings);
  const [identity, setIdentity] = useState<IdentitySettings>(loadIdentitySettings);
  const [activeSection, setActiveSection] = useState<string>('identity');
  const [previewMode, setPreviewMode] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const colorSave      = useSectionSave(onToast);
  const fontSave       = useSectionSave(onToast);
  const layoutSave     = useSectionSave(onToast);
  const componentsSave = useSectionSave(onToast);
  const navbarSave     = useSectionSave(onToast);
  const footerSave     = useSectionSave(onToast);
  const identitySave   = useSectionSave(onToast);
  const sectionsSave   = useSectionSave(onToast);

  const selectedColor  = COLOR_PRESETS.find(p => p.id === design.colorPresetId) ?? COLOR_PRESETS[0];
  const selectedFont   = FONT_OPTIONS.find(f => f.id === design.fontId) ?? FONT_OPTIONS[0];
  const selectedRadius = RADIUS_OPTIONS.find(r => r.id === design.radiusId) ?? RADIUS_OPTIONS[2];
  const selectedFooter = FOOTER_STYLES.find(f => f.id === brand.footerStyle) ?? FOOTER_STYLES[0];
  const selectedHeroPreset = HERO_IMAGE_PRESETS.find(h => h.id === identity.heroImageId) ?? HERO_IMAGE_PRESETS[0];

  useEffect(() => {
    const fonts = FONT_OPTIONS.map(f => f.name.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fonts}&display=swap`;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const updateDesign = (patch: Partial<DesignSettings>) => {
    setDesign(prev => { const u = { ...prev, ...patch }; saveDesignSettings(u); return u; });
  };
  const updateBrand = (patch: Partial<BrandSettings>) => {
    setBrand(prev => { const u = { ...prev, ...patch }; saveBrandSettings(u); return u; });
  };
  const updateIdentity = (patch: Partial<IdentitySettings>) => {
    setIdentity(prev => { const u = { ...prev, ...patch }; saveIdentitySettings(u); return u; });
  };

  /** Notify the live site and refresh iframe after save */
  const afterSave = () => {
    notifyDesignChanged();
    // Reload iframe to reflect changes
    setIframeKey(k => k + 1);
  };

  const handleSaveColors = () => {
    colorSave.save(async () => {
      await updateSettings({ colorPresetId: design.colorPresetId });
      applyColorPreset(COLOR_PRESETS.find(p => p.id === design.colorPresetId) ?? COLOR_PRESETS[0]);
      afterSave();
    }, 'تم حفظ الألوان ✓');
  };
  const handleSaveFont = () => fontSave.save(() => {
    document.documentElement.style.setProperty('--font-primary', selectedFont.css);
    saveDesignSettings(design);
    afterSave();
  }, 'تم حفظ الخط ✓');
  const handleSaveLayout = () => layoutSave.save(() => { saveDesignSettings(design); afterSave(); }, 'تم حفظ التخطيط ✓');
  const handleSaveComponents = () => componentsSave.save(() => { saveDesignSettings(design); afterSave(); }, 'تم حفظ المكونات ✓');
  const handleSaveNavbar = () => navbarSave.save(() => { saveBrandSettings(brand); afterSave(); }, 'تم حفظ الـ Navbar ✓');
  const handleSaveFooter = () => footerSave.save(() => { saveBrandSettings(brand); afterSave(); }, 'تم حفظ الفوتر ✓');
  const handleSaveIdentity = () => identitySave.save(async () => {
    saveIdentitySettings(identity);
    await updateSettings({ logoText: identity.logoText });
    afterSave();
  }, 'تم حفظ الهوية البصرية ✓');
  const handleSaveSections = () => sectionsSave.save(() => { saveDesignSettings(design); afterSave(); }, 'تم حفظ ألوان الأقسام ✓');

  const navbarPreviewBg = brand.navbarStyle === 'colored' ? selectedColor.primary
    : brand.navbarStyle === 'glass' ? 'rgba(10,10,10,0.6)'
    : brand.navbarStyle === 'light' ? '#ffffff'
    : brand.navbarStyle === 'transparent' ? 'transparent'
    : '#0a0a0a';
  const navbarPreviewText = brand.navbarStyle === 'light' ? '#1a1a1a' : '#ffffff';

  const sections = [
    { id: 'identity',   label: 'الشعار والصور',  icon: 'ri-image-edit-line'  },
    { id: 'brand',      label: 'Navbar & Footer', icon: 'ri-shield-star-line' },
    { id: 'colors',     label: 'الألوان',         icon: 'ri-palette-line'     },
    { id: 'typography', label: 'الخطوط',          icon: 'ri-text'             },
    { id: 'layout',     label: 'التخطيط',         icon: 'ri-layout-3-line'    },
    { id: 'components', label: 'المكونات',        icon: 'ri-puzzle-line'      },
    { id: 'sections',   label: 'الأقسام',         icon: 'ri-layout-3-line'    },
    { id: 'preview',    label: 'معاينة',          icon: 'ri-eye-line'         },
  ];

  return (
    <div className="flex flex-col" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-black text-[#1a1a1a] flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: `linear-gradient(135deg, ${selectedColor.primaryLight}, ${selectedColor.primary})` }}>
              <i className="ri-magic-line text-sm" style={{ color: selectedColor.textOnPrimary }}></i>
            </div>
            تصميم الموقع
          </h2>
          <p className="text-xs text-gray-400 mt-1">خصّص مظهر متجرك — شعار، صور، ألوان، خطوط، تخطيط</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-gray-200 text-gray-600 hover:border-gray-400 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line text-sm"></i>
            فتح المتجر
          </a>
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${previewMode ? 'bg-[#1a1a1a] text-white' : 'border border-gray-200 text-gray-600 hover:border-gray-400'}`}
          >
            <i className={previewMode ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            {previewMode ? 'إخفاء المعاينة' : 'معاينة مباشرة'}
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 mb-6 overflow-x-auto">
        {sections.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${activeSection === s.id ? 'bg-white text-[#1a1a1a]' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <i className={`${s.icon} text-sm`}></i>
            <span className="hidden sm:inline">{s.label}</span>
          </button>
        ))}
      </div>

      <div className={`grid gap-6 ${previewMode ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Settings Panel */}
        <div className="flex flex-col gap-5">
          {activeSection === 'identity' && (
            <IdentitySection
              identity={identity} design={design}
              updateIdentity={updateIdentity}
              identitySaveState={identitySave.state}
              onSaveIdentity={handleSaveIdentity}
              selectedColor={selectedColor}
              selectedFont={selectedFont}
              selectedRadius={selectedRadius}
            />
          )}
          {activeSection === 'brand' && (
            <BrandSection
              brand={brand} updateBrand={updateBrand}
              navbarSaveState={navbarSave.state} footerSaveState={footerSave.state}
              onSaveNavbar={handleSaveNavbar} onSaveFooter={handleSaveFooter}
              selectedColor={selectedColor}
            />
          )}
          {activeSection === 'colors' && (
            <ColorsSection
              design={design} updateDesign={updateDesign}
              colorSaveState={colorSave.state} onSaveColors={handleSaveColors}
              selectedColor={selectedColor}
            />
          )}
          {activeSection === 'typography' && (
            <TypographySection
              design={design} updateDesign={updateDesign}
              fontSaveState={fontSave.state} onSaveFont={handleSaveFont}
            />
          )}
          {activeSection === 'layout' && (
            <LayoutSection
              design={design} updateDesign={updateDesign}
              layoutSaveState={layoutSave.state} onSaveLayout={handleSaveLayout}
              selectedColor={selectedColor}
            />
          )}
          {activeSection === 'components' && (
            <ComponentsSection
              design={design} updateDesign={updateDesign}
              componentsSaveState={componentsSave.state} onSaveComponents={handleSaveComponents}
              selectedColor={selectedColor} selectedRadius={selectedRadius}
            />
          )}
          {activeSection === 'sections' && (
            <SectionsSection
              design={design} updateDesign={updateDesign}
              sectionsSaveState={sectionsSave.state} onSaveSections={handleSaveSections}
              selectedColor={selectedColor}
            />
          )}

          {/* Preview Summary */}
          {activeSection === 'preview' && (
            <div className="flex flex-col gap-5">
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                <h3 className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
                    <i className="ri-eye-line text-white text-[10px]"></i>
                  </div>
                  ملخص التصميم الحالي
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'اسم المتجر',      value: identity.logoText || 'إبداع',                                                  dot: null },
                    { label: 'اللون الرئيسي',   value: selectedColor.name,                                                             dot: selectedColor.primary },
                    { label: 'الخط',             value: selectedFont.label,                                                             dot: null },
                    { label: 'الزوايا',          value: selectedRadius.label,                                                           dot: null },
                    { label: 'نمط الأزرار',      value: BUTTON_STYLES.find(b => b.id === design.buttonStyleId)?.label ?? '—',          dot: null },
                    { label: 'شكل الـ Navbar',   value: NAVBAR_STYLES.find(n => n.id === brand.navbarStyle)?.label ?? '—',            dot: null },
                    { label: 'لون الفوتر',       value: FOOTER_STYLES.find(f => f.id === brand.footerStyle)?.label ?? '—',            dot: selectedFooter.bg },
                    { label: 'صورة الهيرو',      value: HERO_IMAGE_PRESETS.find(h => h.id === identity.heroImageId)?.label ?? '—',    dot: null },
                    { label: 'التخطيط',          value: LAYOUT_OPTIONS.find(l => l.id === design.layoutId)?.label ?? '—',             dot: null },
                    { label: 'أعمدة المنتجات',   value: `${design.productGridCols} أعمدة`,                                            dot: null },
                  ].map(item => (
                    <div key={item.label} className="bg-white rounded-xl p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-400 mb-1">{item.label}</p>
                      <div className="flex items-center gap-2">
                        {item.dot && <div className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-200" style={{ background: item.dot }}></div>}
                        <p className="text-sm font-bold text-[#1a1a1a]">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                <h3 className="text-sm font-bold text-[#1a1a1a] mb-4">إجراءات سريعة</h3>
                <div className="flex flex-col gap-2">
                  <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-all cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a] rounded-lg"><i className="ri-store-2-line text-white text-sm"></i></div>
                    <div><p className="text-sm font-bold text-[#1a1a1a]">عرض المتجر</p><p className="text-xs text-gray-400">افتح الموقع في تبويب جديد</p></div>
                    <i className="ri-external-link-line text-gray-400 mr-auto"></i>
                  </a>
                  <button
                    onClick={() => {
                      setDesign(DEFAULT_DESIGN); setBrand(DEFAULT_BRAND); setIdentity(DEFAULT_IDENTITY);
                      saveDesignSettings(DEFAULT_DESIGN); saveBrandSettings(DEFAULT_BRAND); saveIdentitySettings(DEFAULT_IDENTITY);
                      applyColorPreset(COLOR_PRESETS.find(p => p.id === DEFAULT_DESIGN.colorPresetId) ?? COLOR_PRESETS[0]);
                      notifyDesignChanged();
                      setIframeKey(k => k + 1);
                      onToast('تم استعادة التصميم الافتراضي');
                    }}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-red-200 hover:bg-red-50 transition-all cursor-pointer text-right"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg"><i className="ri-refresh-line text-red-500 text-sm"></i></div>
                    <div><p className="text-sm font-bold text-red-600">استعادة التصميم الافتراضي</p><p className="text-xs text-gray-400">إعادة ضبط كل إعدادات التصميم والهوية</p></div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Live Preview Panel — Real iframe */}
        {previewMode && (
          <div className="flex flex-col gap-4">
            <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden sticky top-20">
              {/* Preview toolbar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                  </div>
                  <span className="text-xs text-gray-400 font-mono mr-2">متجرك — معاينة مباشرة</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIframeKey(k => k + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    title="تحديث المعاينة"
                  >
                    <i className="ri-refresh-line text-sm"></i>
                  </button>
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    title="فتح في تبويب جديد"
                  >
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>

              {/* Color quick-pick bar */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-gray-100 overflow-x-auto">
                <span className="text-[10px] text-gray-400 font-bold whitespace-nowrap flex-shrink-0">لون سريع:</span>
                {COLOR_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => {
                      updateDesign({ colorPresetId: preset.id });
                      updateSettings({ colorPresetId: preset.id });
                      applyColorPreset(preset);
                      notifyDesignChanged();
                      setIframeKey(k => k + 1);
                    }}
                    title={preset.name}
                    className={`w-6 h-6 rounded-full flex-shrink-0 border-2 transition-all cursor-pointer ${design.colorPresetId === preset.id ? 'border-[#1a1a1a] scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ background: preset.primary }}
                  />
                ))}
              </div>

              {/* iframe */}
              <div className="relative" style={{ height: '600px' }}>
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src="/"
                  title="معاينة المتجر"
                  className="w-full h-full border-0"
                  style={{ transform: 'scale(0.75)', transformOrigin: 'top right', width: '133.33%', height: '133.33%' }}
                />
                {/* Overlay to prevent interaction */}
                <div className="absolute inset-0 cursor-not-allowed" style={{ background: 'transparent' }}></div>
              </div>

              <div className="px-4 py-3 bg-white border-t border-gray-100 flex items-center justify-between">
                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                  <i className="ri-information-line text-amber-500"></i>
                  احفظ التغييرات لتظهر في المعاينة
                </p>
                <button
                  onClick={() => setIframeKey(k => k + 1)}
                  className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-refresh-line text-xs"></i>
                  تحديث
                </button>
              </div>
            </div>

            {/* Quick design summary */}
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 mb-3">التصميم الحالي</p>
              <div className="flex flex-wrap gap-2">
                <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-100 bg-gray-50">
                  <div className="w-3 h-3 rounded-full" style={{ background: selectedColor.primary }}></div>
                  {selectedColor.name}
                </span>
                <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-100 bg-gray-50">
                  <i className="ri-text text-gray-400 text-xs"></i>
                  {selectedFont.label}
                </span>
                <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-100 bg-gray-50">
                  <i className="ri-corner-down-right-line text-gray-400 text-xs"></i>
                  {selectedRadius.label}
                </span>
                <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-gray-100 bg-gray-50">
                  <i className="ri-layout-top-line text-gray-400 text-xs"></i>
                  {NAVBAR_STYLES.find(n => n.id === brand.navbarStyle)?.label ?? '—'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
